import { Injectable, inject } from '@angular/core'
import { ProductPackaging } from '@tradecafe/types/core'
import { pick } from 'lodash-es'
import { AuthApiService } from 'src/api/auth'
import { ProductPackagingApiService } from 'src/api/product/packaging'

const ALLOWED_FIELDS = ['name', 'description', 'attributes']
const CACHE_MAX_AGE = 3600 * 1000 // 1hr

/**
 * PackageTypes service
 *
 * @export
 * @returns
 */
@Injectable()
export class PackageTypesService {
  private AuthApi = inject(AuthApiService)
  private ProductPackagingApi = inject(ProductPackagingApiService)


  private cache = Promise.resolve<{ data: ProductPackaging[] }>({ data: [] })
  private cacheExpiration = 0


  /**
   * Get all available packageTypes
   */
  async getPackageTypes() {
    if (!this.cache || this.cacheExpiration < Date.now()) {
      this.cacheExpiration = Date.now() + CACHE_MAX_AGE // 1 min
      // NOTE: we fetch everything
      this.cache = this.ProductPackagingApi.list({ limit: Number.MAX_SAFE_INTEGER })
      .then(async (packageTypes) => {
        packageTypes.data.forEach(packageType => {
          packageType.updated = packageType.updated || packageType.created
        })
        return packageTypes
      })
      .catch((err) => {
        this.invalidateCache()
        throw err
      })
    }
    const { data } = await this.cache
    return data
  }

  /**
   * Create new packageType
   *
   * @param {any} packageType
   * @returns
   */
  async create(packageType: Partial<ProductPackaging>) {
    const payload = pick(packageType, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser
    payload.user_id = user_id

    const { data } = await this.ProductPackagingApi.create(payload)
    this.invalidateCache()
    return data
  }

  /**
   * Update packageType
   *
   * @param {any} packageType
   * @returns
   */
  async update(packageType: Partial<ProductPackaging>) {
    const { package_id } = packageType
    const payload = pick(packageType, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser
    payload.user_id = user_id

    const { data } = await this.ProductPackagingApi.update(package_id, payload)
    this.invalidateCache()
    return data
  }

  /**
   * Update packageType
   *
   * @param {any} packageType
   * @returns
   */
  async remove(package_id: string) {
    const { data } = await this.ProductPackagingApi.delete(package_id)
    this.invalidateCache()
    return data
  }


  /**
   * Invalidate packageTypes in-memory cache
   *
   * @private
   */
  private invalidateCache() {
    this.cacheExpiration = 0
  }
}
